import { AlertTriangle, Lock, type LucideIcon } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';

/** Skeleton no formato do conteúdo. Nunca spinner de página. */
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden
      className={cn('h-3 animate-pulse rounded-sm bg-surface-2', className)}
      {...props}
    />
  );
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="w-[38%]" />
          <Skeleton className="w-[14%]" />
          <Skeleton className="w-[18%]" />
          <Skeleton className="w-[10%]" />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2.5 px-6 py-10 text-center text-text-2',
        className,
      )}
    >
      <Icon className="size-8 text-text-3" strokeWidth={1.5} aria-hidden />
      <p className="font-medium text-text-1">{title}</p>
      {description ? <p className="max-w-sm text-sm">{description}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  message,
  code,
  onRetry,
  className,
}: {
  message?: string;
  code?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2.5 px-6 py-10 text-center text-text-2',
        className,
      )}
    >
      <AlertTriangle className="size-8 text-danger" strokeWidth={1.5} aria-hidden />
      <p className="font-medium text-text-1">Não foi possível carregar</p>
      {message ? <p className="max-w-sm text-sm">{message}</p> : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-1">
          Tentar novamente
        </Button>
      ) : null}
      {code ? <span className="font-mono text-xs text-text-3">{code}</span> : null}
    </div>
  );
}

export function ForbiddenState({ className }: { className?: string }) {
  return (
    <EmptyState
      icon={Lock}
      title="Sem permissão"
      description="Você não tem acesso a esta área. Fale com um administrador da organização."
      className={className}
    />
  );
}

/** Label de seção em mono caps (skill: `.label-caps`). */
export function LabelCaps({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('label-caps', className)} {...props} />;
}

/** Atalho de teclado. */
export function Kbd({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <kbd
      className={cn(
        'rounded-sm border border-border-strong bg-surface-2 px-1.5 py-px font-mono text-[10px] text-text-3',
        className,
      )}
      {...props}
    />
  );
}
